'use client'

import React from 'react'
import type { Media } from '@/payload-types'

export type KnowledgePreviewImage = {
  image?: Media | string | null
  caption?: string | null
}

type Props = {
  images: KnowledgePreviewImage[]
  heading?: string
  /**
   * Whether a thumbnail opens the full-size lightbox. Off for entries whose preview
   * pages are the payoff of the gated download (an equipment shortlist, a pricing
   * table): the reader should see that the page exists, not be able to read it.
   */
  zoomable?: boolean
}

const urlOf = (m: Media): string =>
  m.sizes?.large?.url || m.sizes?.medium?.url || (m.url as string)

const thumbOf = (m: Media): string =>
  m.sizes?.small?.url || m.sizes?.medium?.url || (m.url as string)

/**
 * Document preview gallery shown above the gated downloads. Lets logged-out
 * visitors flip through page screenshots before signing in. Click a thumbnail
 * to open a lightbox; Esc / click-outside closes.
 */
export const KnowledgeDownloadPreview: React.FC<Props> = ({
  images,
  heading = 'Preview',
  zoomable = true,
}) => {
  const items = (images ?? []).filter(
    (i): i is KnowledgePreviewImage & { image: Media } =>
      Boolean(i?.image) && typeof i.image === 'object' && Boolean((i.image as Media).url),
  )
  const [active, setActive] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (!zoomable && active !== null) setActive(null)
  }, [zoomable, active])

  React.useEffect(() => {
    if (active === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(null)
      if (e.key === 'ArrowRight') setActive((a) => (a === null ? a : (a + 1) % items.length))
      if (e.key === 'ArrowLeft')
        setActive((a) => (a === null ? a : (a - 1 + items.length) % items.length))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active, items.length])

  if (items.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      <p className="text-text-base text-body-S-semibold">{heading}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map((item, i) => {
          const m = item.image as Media
          const Wrapper = zoomable ? 'button' : 'div'
          return (
            <Wrapper
              key={m.id ?? i}
              {...(zoomable
                ? {
                    type: 'button' as const,
                    onClick: () => setActive(i),
                    'aria-label': item.caption || `Open preview ${i + 1}`,
                  }
                : {})}
              className={
                'group relative block overflow-hidden rounded-md border border-border-default bg-bg-base aspect-[3/4] focus:outline-none focus-visible:ring-2 focus-visible:ring-border-strong' +
                (zoomable ? '' : ' select-none')
              }
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={thumbOf(m)}
                alt={item.caption || m.alt || `Preview ${i + 1}`}
                className={
                  'h-full w-full object-cover object-top transition-transform duration-200' +
                  (zoomable ? ' group-hover:scale-[1.03]' : ' pointer-events-none')
                }
                loading="lazy"
                draggable={false}
              />
              {item.caption && (
                <span className="absolute inset-x-0 bottom-0 bg-black/55 px-2 py-1 text-left text-[11px] leading-tight text-white">
                  {item.caption}
                </span>
              )}
            </Wrapper>
          )
        })}
      </div>

      {active !== null && items[active] && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setActive(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute right-4 top-4 text-white/90 text-2xl leading-none"
            onClick={() => setActive(null)}
            aria-label="Close preview"
          >
            ✕
          </button>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={urlOf(items[active]!.image as Media)}
            alt={items[active]!.caption || `Preview ${active + 1}`}
            className="max-h-[90vh] max-w-[92vw] rounded-md object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          {items.length > 1 && (
            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-body-XS-regular">
              {active + 1} / {items.length}
              {items[active]!.caption ? ` — ${items[active]!.caption}` : ''}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
